import { isObject } from '@scalar/helpers/object/is-object';
import { getResolvedRef } from './get-resolved-ref.js';
const schemaMaps = new Set(['properties', 'patternProperties', '$defs', 'definitions', 'dependentSchemas']);
const dataFields = new Set(['example', 'examples', 'default', 'enum', 'const', 'value', 'dataValue']);
/**
 * Apply JSON Merge Patch to resolved trait fields. Unlike mergeObjects, null removes a key.
 * Resolve schema references before merging so a message can extend referenced trait headers.
 */
const mergeTraitFields = (target, patch, context = 'object', ancestors = new WeakMap()) => {
    // Reply channels identify a location in the document, so retain their reference wrappers.
    if (context === 'reference') {
        return patch;
    }
    const resolvedPatch = context === 'object' && isObject(patch) && typeof patch.$ref === 'string' ? getResolvedRef(patch) : patch;
    if (!isObject(resolvedPatch)) {
        return resolvedPatch;
    }
    // Resolved schemas can be recursive. Keep their cycles without changing the source document.
    if (ancestors.has(resolvedPatch)) {
        return ancestors.get(resolvedPatch);
    }
    const resolvedTarget = context === 'object' && isObject(target) && typeof target.$ref === 'string' ? getResolvedRef(target) : target;
    const result = isObject(resolvedTarget) ? { ...resolvedTarget } : {};
    ancestors.set(resolvedPatch, result);
    for (const [key, value] of Object.entries(resolvedPatch)) {
        if (value === null) {
            delete result[key];
        }
        else if (value !== undefined) {
            // Schema names and example data can contain literal $ref keys without being references.
            const childContext = context === 'data'
                ? 'data'
                : context === 'map'
                    ? 'object'
                    : key === 'channel'
                        ? 'reference'
                        : dataFields.has(key) || key.startsWith('x-')
                            ? 'data'
                            : schemaMaps.has(key)
                                ? 'map'
                                : 'object';
            // Define an own property even for schema properties named __proto__.
            Object.defineProperty(result, key, {
                value: mergeTraitFields(Object.hasOwn(result, key) ? result[key] : undefined, value, childContext, ancestors),
                enumerable: true,
                configurable: true,
                writable: true,
            });
        }
    }
    ancestors.delete(resolvedPatch);
    return result;
};
/** Merge traits in order, then apply the target's own fields with highest priority. */
export const resolveTraits = (target, traits) => {
    if (traits.length === 0) {
        return target;
    }
    const inherited = traits.reduce((fields, trait) => {
        const resolved = getResolvedRef(trait);
        return resolved ? mergeTraitFields(fields, resolved) : fields;
    }, {});
    // Only overlay fields supplied by traits. Unrelated fields keep their references and identity.
    const overrides = Object.fromEntries(Object.entries(target).filter(([key]) => Object.hasOwn(inherited, key)));
    const merged = mergeTraitFields(inherited, overrides);
    const ownFields = Object.fromEntries(Object.entries(target).filter(([key]) => !Object.hasOwn(inherited, key)));
    return { ...ownFields, ...merged };
};
